import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useNetwork } from '@/contexts/NetworkContext';
import { supabase } from '@/integrations/supabase/client';
import {
  cacheCatalog,
  stripCachedCatalogMedia,
  cacheBundles,
  cacheClients,
  cacheQuotes,
  cacheServiceOrders,
  cacheAppointments,
  cacheContracts,
  cacheReceipts,
  cacheMessageTemplates,
  getCachedCatalog,
  getCachedClients,
  getCachedQuotes,
  getCachedServiceOrders,
  getCachedAppointments,
  getCachedContracts,
  getCachedReceipts,
} from '@/lib/offlineDataCache';
import { hasOfflineOperations } from '@/lib/offlineStorage';
import { warmImageBytesForOffline } from '@/lib/mediaUrl';

function pendingLocalRecords(records: any[]): any[] {
  return records.filter(
    (record) => record?._offline_pending === true && String(record.id).startsWith('offline_'),
  );
}

function mergeServerRecords(serverRecords: any[], cachedRecords: any[]): any[] {
  const serverIds = new Set(serverRecords.map((record) => record.id));
  return [
    ...serverRecords,
    ...pendingLocalRecords(cachedRecords).filter((record) => !serverIds.has(record.id)),
  ];
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/**
 * Bulk-fetches child rows (quote_items / quote_photos) for many quotes at once
 * and groups them by quote_id. Chunks the `.in(...)` filter so a large company
 * never blows the request URL length. Returns `ok: false` if any chunk failed,
 * so the caller can keep previously cached nested data instead of wiping it.
 */
async function fetchChildrenByQuote(
  table: 'quote_items' | 'quote_photos',
  quoteIds: string[],
  mapRow: (row: any) => any,
): Promise<{ byQuote: Map<string, any[]>; ok: boolean }> {
  const byQuote = new Map<string, any[]>();
  if (quoteIds.length === 0) return { byQuote, ok: true };

  let ok = true;
  for (const ids of chunk(quoteIds, 200)) {
    const { data, error } = await supabase
      .from(table)
      .select('*')
      .in('quote_id', ids)
      .order('position', { ascending: true });

    if (error || !data) {
      ok = false;
      continue;
    }

    for (const row of data as any[]) {
      const list = byQuote.get(row.quote_id) || [];
      list.push(mapRow(row));
      byQuote.set(row.quote_id, list);
    }
  }

  return { byQuote, ok };
}

// Shapes below mirror what QuoteDetailPage reads back from `_offline_items` /
// `_offline_photos`, so a warmed quote opens offline exactly like one that was
// opened individually while online.
const mapItemForCache = (row: any) => ({
  id: row.id,
  name: row.name,
  description: row.description,
  quantity: row.quantity,
  unit_price: row.unit_price,
  // Snapshot do custo (armadilha #62): sem ele, um orçamento aquecido por aqui
  // é reaberto sem custo e o próximo save grava `null`, apagando o custo
  // histórico da linha em silêncio.
  unit_cost: row.unit_cost,
  discount: row.discount,
  discount_type: row.discount_type,
  image_url: row.image_url,
  image_url_2: row.image_url_2,
  image_url_3: row.image_url_3,
  item_type: row.item_type,
  unit: row.unit,
  product_id: row.product_id,
  // Vínculo com a despesa cobrável (mesmo modo de falha do snapshot acima): um
  // orçamento aquecido sem ele é reaberto com a linha "solta", e o próximo save
  // grava `finance_entry_id` null — o financeiro perde o controle da despesa.
  finance_entry_id: row.finance_entry_id ?? null,
});

const mapPhotoForCache = (row: any) => ({
  id: row.id,
  image_url: row.image_url,
  imageUrl: row.image_url,
  title: row.title,
  description: row.description,
});

/**
 * Drops heavy base64 `data:` image payloads from a quote's cached nested data.
 * Used only as a fallback when the full snapshot exceeds the localStorage quota:
 * item/photo metadata (names, prices, titles) stays visible offline; only the
 * inline image bytes are sacrificed. Storage-hosted images (short URLs/paths)
 * are untouched.
 */
function stripHeavyMedia(quote: any): any {
  const isDataUrl = (v: any) => typeof v === 'string' && v.startsWith('data:');
  const items = Array.isArray(quote._offline_items)
    ? quote._offline_items.map((it: any) =>
        isDataUrl(it?.image_url) ? { ...it, image_url: null } : it,
      )
    : quote._offline_items;
  const photos = Array.isArray(quote._offline_photos)
    ? quote._offline_photos.map((p: any) =>
        isDataUrl(p?.imageUrl) || isDataUrl(p?.image_url)
          ? { ...p, imageUrl: null, image_url: null }
          : p,
      )
    : quote._offline_photos;
  return { ...quote, _offline_items: items, _offline_photos: photos };
}

/**
 * Quota fallback for a service order: nulls inline base64 image bytes on the
 * offline-pending nested arrays, keeping item/photo metadata. Mirrors
 * stripHeavyMedia. Synced rows carry short Storage URLs and are untouched.
 */
function stripHeavyServiceOrderMedia(order: any): any {
  const isDataUrl = (v: any) => typeof v === 'string' && v.startsWith('data:');
  const items = Array.isArray(order._offline_items)
    ? order._offline_items.map((it: any) =>
        isDataUrl(it?.image_url) ? { ...it, image_url: null } : it,
      )
    : order._offline_items;
  const photos = Array.isArray(order._offline_photos)
    ? order._offline_photos.map((p: any) =>
        isDataUrl(p?.imageUrl) || isDataUrl(p?.image_url)
          ? { ...p, imageUrl: null, image_url: null }
          : p,
      )
    : order._offline_photos;
  return { ...order, _offline_items: items, _offline_photos: photos };
}

/**
 * Keeps the main list snapshots ready for offline navigation, even when the
 * user has not opened each page during the current online session.
 */
export function OfflineDataCacheWarmup() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const { isOnline, isSyncing, pendingOperations } = useNetwork();
  // Keyed by user+company so switching companies re-warms the correct snapshot.
  const warmedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user?.id || !activeCompany || !isOnline || isSyncing || pendingOperations > 0) return;
    if (hasOfflineOperations()) return;
    const warmKey = `${user.id}:${activeCompany.id}`;
    if (warmedForRef.current === warmKey) return;

    let cancelled = false;

    const warmCache = async () => {
      const [quotesResult, clientsResult, catalogResult, serviceOrdersResult, appointmentsResult, contractsResult, receiptsResult, messageTemplatesResult, bundlesResult] = await Promise.all([
        supabase
          .from('quotes')
          .select('*')
          .eq('company_id', activeCompany.id)
          .order('created_at', { ascending: false }),
        supabase
          .from('clients')
          .select('*')
          .eq('company_id', activeCompany.id)
          .order('name'),
        supabase
          .from('products')
          .select('*, product_photos(*)')
          .eq('company_id', activeCompany.id)
          .order('name'),
        // Service orders bring their child items/photos via an embedded join, so
        // a warmed SO opens offline with full content (no separate child fetch).
        supabase
          .from('service_orders')
          .select('*, service_order_items(*), service_order_photos(*)')
          .eq('company_id', activeCompany.id)
          .order('created_at', { ascending: false }),
        // Appointments (agenda) are flat rows; warm them so the calendar opens
        // offline with the full month even on pages the user hasn't visited.
        supabase
          .from('appointments')
          .select('*')
          .eq('company_id', activeCompany.id)
          .order('starts_at', { ascending: true }),
        // Contracts (flat rows) — warm so the list opens offline. The DRAFT is
        // offline-first; sending/signing are online.
        supabase
          .from('contracts')
          .select('*')
          .eq('company_id', activeCompany.id)
          .order('created_at', { ascending: false }),
        // Receipts (flat rows) — warm so the list opens offline. Fully
        // offline-first (create/update/delete).
        supabase
          .from('receipts')
          .select('*')
          .eq('company_id', activeCompany.id)
          .order('issue_date', { ascending: false }),
        // Modelos de mensagem (flat) — o gerenciamento é online-only, mas a
        // LEITURA precisa estar cacheada: compor a mensagem é local e o usuário
        // tem que conseguir enviar pelo WhatsApp mesmo sem rede.
        supabase
          .from('message_templates')
          .select('*')
          .eq('company_id', activeCompany.id)
          .order('created_at', { ascending: true }),
        // Materiais inclusos (kits) — o vínculo é só configuração, mas montar o
        // orçamento a partir de um serviço-kit precisa funcionar SEM rede: os
        // materiais viram quote_items locais como qualquer item do catálogo.
        supabase
          .from('product_bundle_items')
          .select('*')
          .eq('company_id', activeCompany.id),
      ]);

      if (cancelled) return;

      // Whether the nested items/photos fetch fully succeeded. Gates the
      // "warmed" marker below so a partial child-row failure retries later.
      let nestedOk = true;

      if (!quotesResult.error && quotesResult.data) {
        const cachedQuotes = getCachedQuotes(user.id) || [];
        const cachedById = new Map(cachedQuotes.map((quote: any) => [quote.id, quote]));

        // Fetch the nested items and photos for EVERY quote so each one opens
        // offline with its full content — not just quotes the user happened to
        // open individually while online. Without this, opening an untouched
        // quote offline shows an empty items/photos list until reconnect.
        const quoteIds = quotesResult.data
          .map((quote: any) => quote.id)
          .filter((id: any) => id && !String(id).startsWith('offline_'));
        const [itemsRes, photosRes] = await Promise.all([
          fetchChildrenByQuote('quote_items', quoteIds, mapItemForCache),
          fetchChildrenByQuote('quote_photos', quoteIds, mapPhotoForCache),
        ]);

        if (cancelled) return;

        nestedOk = itemsRes.ok && photosRes.ok;

        const serverQuotes = quotesResult.data.map((quote: any) => {
          const cached = cachedById.get(quote.id) as any;
          return {
            ...quote,
            // Prefer freshly fetched children. Fall back to any cached copy only
            // when the fetch failed, so a transient error never wipes nested data.
            _offline_items: itemsRes.ok
              ? itemsRes.byQuote.get(quote.id) || []
              : cached?._offline_items || [],
            _offline_photos: photosRes.ok
              ? photosRes.byQuote.get(quote.id) || []
              : cached?._offline_photos || [],
            _offline_pending: false,
          };
        });

        const merged = mergeServerRecords(serverQuotes, cachedQuotes);
        // On localStorage quota failure (heavy base64 photos), retry with the
        // inline image bytes stripped so item/photo metadata still persists.
        if (!cacheQuotes(user.id, merged)) {
          cacheQuotes(user.id, merged.map(stripHeavyMedia));
        }

        // Warm the actual image BYTES (item photos + general photos) into the
        // IndexedDB media cache so they render offline, not just their records.
        // Best-effort and bounded; failures don't affect the JSON snapshot above.
        const imageUrls: string[] = [];
        // Company logo: needed so offline-generated PDFs still show the brand.
        if (activeCompany.logo_url) imageUrls.push(activeCompany.logo_url);
        for (const quote of serverQuotes) {
          for (const it of quote._offline_items || []) {
            if (it?.image_url) imageUrls.push(it.image_url);
          }
          for (const ph of quote._offline_photos || []) {
            if (ph?.image_url) imageUrls.push(ph.image_url);
          }
        }
        if (imageUrls.length > 0) {
          const { capped } = await warmImageBytesForOffline(imageUrls);
          if (cancelled) return;
          if (capped > 0) {
            console.info(
              `[OfflineDataCacheWarmup] Warmed image cap reached; ${capped} image(s) left for lazy caching on view.`,
            );
          }
        }
      }

      if (!clientsResult.error && clientsResult.data) {
        const cachedClients = getCachedClients(user.id) || [];
        cacheClients(user.id, mergeServerRecords(clientsResult.data, cachedClients));
      }

      if (!catalogResult.error && catalogResult.data) {
        const cachedCatalog = getCachedCatalog(user.id) || [];
        const mergedCatalog = mergeServerRecords(catalogResult.data, cachedCatalog);
        // Mesmo retry do bloco de orçamentos acima: um item cadastrado offline
        // guarda a foto em base64, e é ele que estoura a cota. Sem o retry a
        // gravação inteira é recusada e o snapshot do catálogo PARA de ser
        // atualizado enquanto esse item pendente existir — em silêncio.
        if (!cacheCatalog(user.id, mergedCatalog)) {
          cacheCatalog(user.id, mergedCatalog.map(stripCachedCatalogMedia));
        }
        const catalogImageUrls = catalogResult.data.flatMap((product: any) => [
          product.image_url,
          ...(product.product_photos ?? []).map((photo: any) => photo.image_url),
        ]).filter(Boolean);
        if (catalogImageUrls.length > 0) {
          await warmImageBytesForOffline(catalogImageUrls);
          if (cancelled) return;
        }
      }

      // Kits: substituição integral, não merge. A ligação não é offline-first
      // (criar/remover exige rede), então o servidor é a verdade completa — um
      // merge manteria vivo localmente um material que já foi desvinculado.
      if (!bundlesResult.error && bundlesResult.data) {
        cacheBundles(user.id, bundlesResult.data);
      }

      if (!serviceOrdersResult.error && serviceOrdersResult.data) {
        const cachedOrders = getCachedServiceOrders(user.id) || [];
        const merged = mergeServerRecords(serviceOrdersResult.data, cachedOrders);
        if (!cacheServiceOrders(user.id, merged)) {
          cacheServiceOrders(user.id, merged.map(stripHeavyServiceOrderMedia));
        }

        // Warm SO image bytes (item + service photos) into the IndexedDB media
        // cache so they render offline. Best-effort and bounded.
        const soImageUrls: string[] = [];
        for (const order of serviceOrdersResult.data as any[]) {
          for (const it of order.service_order_items || []) {
            if (it?.image_url) soImageUrls.push(it.image_url);
          }
          for (const ph of order.service_order_photos || []) {
            if (ph?.image_url) soImageUrls.push(ph.image_url);
          }
        }
        if (soImageUrls.length > 0) {
          await warmImageBytesForOffline(soImageUrls);
          if (cancelled) return;
        }
      }

      if (!appointmentsResult.error && appointmentsResult.data) {
        const cachedAppointments = getCachedAppointments(user.id) || [];
        cacheAppointments(user.id, mergeServerRecords(appointmentsResult.data, cachedAppointments));
      }

      if (!contractsResult.error && contractsResult.data) {
        const cachedContracts = getCachedContracts(user.id) || [];
        cacheContracts(user.id, mergeServerRecords(contractsResult.data, cachedContracts));
      }

      if (!receiptsResult.error && receiptsResult.data) {
        const cachedReceipts = getCachedReceipts(user.id) || [];
        cacheReceipts(user.id, mergeServerRecords(receiptsResult.data, cachedReceipts));
      }

      // Sem `mergeServerRecords`: modelos nunca têm registro local pendente
      // (gerenciamento online-only), então o servidor é a verdade absoluta —
      // e um modelo excluído em outro device tem que sumir daqui.
      if (!messageTemplatesResult.error && messageTemplatesResult.data) {
        cacheMessageTemplates(user.id, messageTemplatesResult.data);
      }

      // Mark the warmup complete only when all snapshots succeeded, including
      // the nested items/photos. A partial network failure may then retry on a
      // later dependency change/remount.
      if (
        !quotesResult.error &&
        !clientsResult.error &&
        !catalogResult.error &&
        !serviceOrdersResult.error &&
        !appointmentsResult.error &&
        !contractsResult.error &&
        !receiptsResult.error &&
        !messageTemplatesResult.error &&
        nestedOk
      ) {
        warmedForRef.current = warmKey;
      }
    };

    void warmCache().catch((error) => {
      console.warn('[OfflineDataCacheWarmup] Could not refresh offline snapshots:', error);
    });

    return () => {
      cancelled = true;
    };
  }, [user?.id, activeCompany?.id, isOnline, isSyncing, pendingOperations]);

  useEffect(() => {
    if (!isOnline) warmedForRef.current = null;
  }, [isOnline]);

  return null;
}
